import {
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
  GiftCouponSummary,
  ShareChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';

export const shareChannelLabel: Record<ShareChannel, string> = {
  [ShareChannel.KakaoTalk]: '카카오톡',
  [ShareChannel.SMS]: '문자',
  [ShareChannel.Link]: '링크',
  [ShareChannel.Other]: '기타',
};

export const redeemStatusLabel: Record<GiftCouponRedeemStatus, string> = {
  [GiftCouponRedeemStatus.Available]: '사용가능',
  [GiftCouponRedeemStatus.Requested]: '교환신청',
  [GiftCouponRedeemStatus.Delivered]: '발송완료',
  [GiftCouponRedeemStatus.Rejected]: '발송불가',
  [GiftCouponRedeemStatus.Expired]: '만료',
  [GiftCouponRedeemStatus.Refunded]: '환불',
  [GiftCouponRedeemStatus.Canceled]: '취소',
  [GiftCouponRedeemStatus.Disposed]: '폐기',
};

export const redeemStatusColor: Record<GiftCouponRedeemStatus, string> = {
  [GiftCouponRedeemStatus.Available]: 'bg-blue-100 text-blue-800',
  [GiftCouponRedeemStatus.Requested]: 'bg-yellow-100 text-yellow-800',
  [GiftCouponRedeemStatus.Delivered]: 'bg-green-100 text-green-800',
  [GiftCouponRedeemStatus.Rejected]: 'bg-orange-100 text-orange-800',
  [GiftCouponRedeemStatus.Expired]: 'bg-gray-100 text-gray-500',
  [GiftCouponRedeemStatus.Refunded]: 'bg-red-100 text-red-800',
  [GiftCouponRedeemStatus.Canceled]: 'bg-gray-100 text-gray-500',
  [GiftCouponRedeemStatus.Disposed]: 'bg-slate-200 text-slate-600',
};

export const shareStatusLabel: Record<GiftCouponShareStatus, string> = {
  [GiftCouponShareStatus.NotShared]: '미공유',
  [GiftCouponShareStatus.Shared]: '공유',
  [GiftCouponShareStatus.Viewed]: '열람',
};

export const shareStatusColor: Record<GiftCouponShareStatus, string> = {
  [GiftCouponShareStatus.NotShared]: 'bg-gray-100 text-gray-500',
  [GiftCouponShareStatus.Shared]: 'bg-indigo-100 text-indigo-700',
  [GiftCouponShareStatus.Viewed]: 'bg-purple-100 text-purple-700',
};

export const fmt = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

export const fmtTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}:${min}`;
};

export const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '';
  const n = phone.replace(/[^0-9]/g, '');
  if (n.length === 11) return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  return phone;
};

export const isCouponCancellable = (c: GiftCouponSummary): boolean =>
  c.shareStatus === GiftCouponShareStatus.NotShared &&
  c.redeemStatus === GiftCouponRedeemStatus.Available;

export const isCouponDisposable = (c: GiftCouponSummary): boolean =>
  c.redeemStatus !== GiftCouponRedeemStatus.Delivered &&
  c.redeemStatus !== GiftCouponRedeemStatus.Disposed &&
  c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
  c.redeemStatus !== GiftCouponRedeemStatus.Refunded;

/** 추가교환으로 생성된 스폰 쿠폰 여부 (중복 공유 → 새 수령자에게 추가 발급) */
export const isSpawnCoupon = (c: GiftCouponSummary): boolean =>
  c.isExtraPaid === true && !!c.spawnedFrom;

/**
 * 스폰 쿠폰 중 아직 정산되지 않은(추가결제/대체발송 처리 전) 건 — 입금·대체발송 대상.
 * 교환신청(Requested)뿐 아니라 "나중에 받기"(Available + 수신번호 확정)도 포함한다.
 * 어차피 수령 예정이고 번호가 확정돼 미리 처리(결제/대체발송)해 둘 수 있기 때문.
 */
export const isSpawnPending = (c: GiftCouponSummary): boolean => {
  if (!isSpawnCoupon(c)) return false;
  if (c.settledBy === 'inventory' || c.settledBy === 'extraPayment') return false;
  if (c.extraPayment?.isPaid) return false;
  if (c.redeemStatus === GiftCouponRedeemStatus.Requested) return true;
  // 나중에 받기: 예약(Available) 상태이지만 수신번호 인증까지 마친 본인 전용 스폰
  return (
    c.redeemStatus === GiftCouponRedeemStatus.Available && !!c.recipient?.phoneNumber
  );
};

/**
 * 추가교환(중복허용)을 열 수 있는 대상 — 이미 교환신청/발송된 쿠폰 (중복 공유 케이스).
 * 단, 추가교환으로 생성된 스폰 쿠폰은 다시 중복허용할 필요가 없어 제외한다.
 */
export const isExtraRedeemEligible = (c: GiftCouponSummary): boolean =>
  !isSpawnCoupon(c) &&
  (c.redeemStatus === GiftCouponRedeemStatus.Requested ||
    c.redeemStatus === GiftCouponRedeemStatus.Delivered);

/** 추가교환 남은 건수 (allowed - used). extraRedeem 없으면 0 */
export const extraRedeemRemaining = (c: GiftCouponSummary): number =>
  c.extraRedeem ? Math.max(0, c.extraRedeem.allowed - c.extraRedeem.used) : 0;

export const resolveGiftItemId = (giftItem: unknown): string | undefined => {
  if (typeof giftItem === 'string') return giftItem;
  if (
    giftItem &&
    typeof giftItem === 'object' &&
    '_id' in giftItem &&
    typeof (giftItem as { _id: unknown })._id === 'string'
  )
    return (giftItem as { _id: string })._id;
  return undefined;
};
